import { navigate } from "../lib/router";
import { Button } from "../components/ui/button";
import { Header } from "../components/Header";
import { SEO } from "../components/SEO";
import { Link, Palette, BarChart3, ArrowRight, CheckCircle2, Sparkles, Check, Zap } from "lucide-react";
import logoIcon from "../assets/icone.png";
import { useState, useEffect } from "react";
import { getPlans, createCheckoutSession } from "../api/subscriptions";
import type { SubscriptionPlan } from "../types";
import { useLanguage } from "../hooks/useLanguage";

export function LandingPage() {
  const { t, language } = useLanguage();
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlanId, setLoadingPlanId] = useState<number | null>(null);

  useEffect(() => {
    // Load plans from API
    getPlans().then(setPlans).catch(console.error);
  }, []);

  const HIGHLIGHTS = [
    {
      icon: Link,
      title: t.features.items[0].title,
      description: t.features.items[0].description,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      icon: Palette,
      title: t.features.items[1].title,
      description: t.features.items[1].description,
      color: "text-purple-600",
      bg: "bg-purple-50",
    },
    {
      icon: BarChart3,
      title: t.features.items[2].title,
      description: t.features.items[2].description,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
  ];

  const FREE_PLAN = {
    name: t.pricing.plans.free.name,
    slug: "free",
    priceMonthly: 0,
    priceAnnual: 0,
    description: t.pricing.plans.free.description,
    features: t.pricing.plans.free.features,
    cta: t.pricing.plans.free.cta,
    popular: false,
    gradient: "from-slate-600 to-slate-700",
    borderColor: "border-slate-200",
    ctaVariant: "ghost" as const,
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handleCheckout = async (planSlug: string) => {
    // Free plan - just register
    if (planSlug === "free") {
      navigate("/register");
      return;
    }

    // Find plan in loaded plans
    const plan = plans.find(p => p.slug === planSlug);
    if (!plan) {
      console.error("Plan not found");
      return;
    }

    try {
      setLoadingPlanId(plan.id);
      const { url } = await createCheckoutSession(plan.id, billingPeriod);
      window.location.href = url;
    } catch (error) {
      console.error("Checkout error:", error);
      // If not logged in or other error, redirect to register to create account first
      navigate("/register");
    } finally {
      setLoadingPlanId(null);
    }
  };

  const getPricingPlans = () => {
    const paidPlans = plans
      .filter(plan => plan.slug !== 'free')
      .map(plan => ({
        name: plan.name,
        slug: plan.slug,
        priceMonthly: plan.price_monthly_cents,
        priceAnnual: plan.price_annual_cents,
        description: plan.slug === 'pro'
          ? t.pricing.plans.pro.description
          : plan.slug === 'premium'
            ? t.pricing.plans.premium.description
            : plan.slug === 'pro' ? t.pricing.plans.pro.description : 'Description not available',
        features: plan.slug === 'pro'
          ? t.pricing.plans.pro.features
          : plan.slug === 'premium'
            ? t.pricing.plans.premium.features
            : Array.isArray(plan.features) ? plan.features : [],
        cta: plan.slug === 'pro'
          ? t.pricing.plans.pro.cta
          : plan.slug === 'premium'
            ? t.pricing.plans.premium.cta
            : plan.slug === 'pro' ? t.pricing.plans.pro.cta : 'Contact sales',
        popular: plan.slug === 'pro',
        gradient: "from-[#0DAFFD] to-[#0FD8FE]",
        borderColor: plan.slug === 'pro' ? "border-brand-primary/20" : "border-purple-200",
        ctaVariant: "default" as const,
      }));

    return [FREE_PLAN, ...paidPlans];
  };

  const getDisplayPrice = (planSlug: string) => {
    const pricingPlans = getPricingPlans();
    const staticPlan = pricingPlans.find(p => p.slug === planSlug);
    if (!staticPlan) return { price: "$0", period: t.pricing.perMonth };

    const price = billingPeriod === "annual"
      ? staticPlan.priceAnnual
      : staticPlan.priceMonthly;

    const period = billingPeriod === "annual" ? t.pricing.perYear : t.pricing.perMonth;

    return {
      price: formatPrice(price),
      period,
    };
  };

  const annualSavings = (monthlyPrice: number, annualPrice: number) => {
    const yearlyMonthly = monthlyPrice * 12;
    const savings = yearlyMonthly - annualPrice;
    return formatPrice(savings);
  };

  return (
    <div className="min-h-screen bg-white selection:bg-indigo-100 selection:text-indigo-900 font-sans">
      <SEO
        title={language === 'en' ? "Hub Links | All your links in one place" : "Hub Links | Seus links em um só lugar"}
        description={language === 'en'
          ? "Hub Links: Centralize your digital presence. Instagram, TikTok, WhatsApp and more in one elegant page. Create your free page!"
          : "Hub Links: Centralize sua presença digital. Instagram, TikTok, WhatsApp e muito mais em uma única página elegante. Crie sua página grátis!"}
        keywords="link na bio, links, redes sociais, instagram, tiktok, página de links, bio link, hub links, link tree"
        ogImage="https://hublinks.click/og-image.jpg"
        ogType="website"
        canonicalUrl="https://hublinks.click/"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Hub Links",
          "url": "https://hublinks.click/",
          "description": language === 'en'
            ? "Centralize your digital presence with Hub Links. Create a personalized page with all your important links."
            : "Centralize sua presença digital com Hub Links. Crie uma página personalizada com todos os seus links importantes.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://hublinks.click/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      {/* Hero Section with Gradient Background */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-300/50 via-white to-white -z-10" />
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 -z-10 mix-blend-overlay"></div>

        <Header />

        {/* Hero Content */}
        <main>
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0DAFFD]/10 border border-[#0DAFFD]/20 text-[#0DAFFD] text-xs font-semibold uppercase tracking-wider shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-700">
              <Sparkles className="w-3 h-3" aria-hidden="true" />
              {t.hero.badge}
            </div>

            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 leading-[1.1] animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100">
              {t.hero.title} <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0DAFFD] via-[#0FD8FE] to-[#313131]">
                {t.hero.titleHighlight}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
              {t.hero.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 animate-in fade-in slide-in-from-bottom-10 duration-700 delay-300">
              <Button
                className="h-14 px-8 text-lg bg-brand-primary hover:bg-brand-primary text-white rounded-xl shadow-xl shadow-brand-primary/20 hover:shadow-2xl hover:shadow-brand-primary/30 transition-all hover:-translate-y-1"
                onClick={() => navigate("/register")}
                aria-label={t.hero.cta}
              >
                {t.hero.cta}
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
              </Button>
              <div className="flex items-center gap-2 text-sm text-slate-500 font-medium">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" aria-hidden="true" />
                {t.hero.noCreditCard}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Spacing */}
      <div className="py-16"></div>

      {/* Features Section */}
      <section className="py-24 bg-slate-50 relative" aria-label={t.features.ariaLabel}>
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="sr-only">Funcionalidades do Hub Links</h2>
          <div className="grid gap-8 md:grid-cols-3">
            {HIGHLIGHTS.map((item, i) => (
              <article
                key={item.title}
                className="group relative bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-xl hover:shadow-indigo-900/5 transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-12 h-12 rounded-2xl ${item.bg} ${item.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`} aria-hidden="true">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-white relative overflow-hidden" aria-label={t.pricing.ariaLabel}>
        {/* Background decoration */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-indigo-300/50 via-white to-white -z-10" />

        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0DAFFD]/10 border border-[#0DAFFD]/20 text-[#0DAFFD] text-xs font-semibold uppercase tracking-wider">
              <Zap className="w-3 h-3" aria-hidden="true" />
              {t.pricing.badge}
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900">
              {t.pricing.title}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t.pricing.subtitle}
            </p>

            {/* Billing Period Toggle */}
            <div className="flex items-center justify-center gap-4 pt-6">
              <button
                onClick={() => setBillingPeriod("monthly")}
                className={`px-6 py-2 rounded-lg font-semibold transition-all ${billingPeriod === "monthly"
                  ? "bg-gradient-to-r from-[#0DAFFD] to-[#0FD8FE] text-white shadow-lg"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
              >
                {t.pricing.monthly}
              </button>
              <button
                onClick={() => setBillingPeriod("annual")}
                className={`px-6 py-2 rounded-lg font-semibold transition-all relative ${billingPeriod === "annual"
                  ? "bg-gradient-to-r from-[#0DAFFD] to-[#0FD8FE] text-white shadow-lg"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
              >
                {t.pricing.annual}
                <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                  -15%
                </span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
            {getPricingPlans().map((plan, index) => (
              <article
                key={plan.name}
                className={`relative bg-white rounded-3xl border-2 ${plan.borderColor} p-8 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-primary/10 hover:-translate-y-2 ${plan.popular ? 'md:scale-105 shadow-xl shadow-brand-primary/10 ring-2 ring-brand-primary' : 'shadow-sm'
                  }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-[#0DAFFD] to-[#0FD8FE] text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-lg">
                    {t.pricing.mostPopular}
                  </div>
                )}

                {/* Plan Header */}
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  <p className="text-slate-600 text-sm">{plan.description}</p>
                </div>

                {/* Price */}
                <div className="mb-8">
                  <div className="flex items-baseline gap-1">
                    <span className={`text-5xl font-extrabold bg-gradient-to-r ${plan.gradient} text-transparent bg-clip-text`}>
                      {getDisplayPrice(plan.slug).price}
                    </span>
                    <span className="text-slate-500 text-lg font-medium">{getDisplayPrice(plan.slug).period}</span>
                  </div>
                  {billingPeriod === "annual" && plan.slug !== "free" && (
                    <p className="text-sm text-emerald-600 font-semibold mt-2">
                      {t.pricing.save} {annualSavings(plan.priceMonthly, plan.priceAnnual)} {t.pricing.perYearSuffix}
                    </p>
                  )}
                </div>

                {/* CTA Button */}
                <Button
                  className={`w-full h-12 rounded-xl font-semibold transition-all duration-300 mb-8 ${plan.ctaVariant === 'default'
                    ? `bg-gradient-to-r ${plan.gradient} text-white hover:shadow-lg hover:shadow-brand-primary/30 hover:-translate-y-0.5`
                    : 'border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white'
                    }`}
                  variant={plan.ctaVariant}
                  onClick={() => handleCheckout(plan.slug)}
                  disabled={loadingPlanId !== null}
                >
                  {loadingPlanId === plans.find(p => p.slug === plan.slug)?.id
                    ? t.pricing.loading
                    : plan.cta}
                </Button>

                {/* Features List */}
                <div className="space-y-4">
                  <div className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
                    {t.pricing.included}
                  </div>
                  <ul className="space-y-3" role="list">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3 text-slate-600">
                        <Check
                          className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.popular ? 'text-brand-primary' : 'text-emerald-500'
                            }`}
                          aria-hidden="true"
                        />
                        <span className="text-sm leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>

          {/* Trust Badge */}
          <div className="mt-16 text-center">
            <p className="text-sm text-slate-500 flex items-center justify-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" aria-hidden="true" />
              {t.pricing.guarantee}
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 pt-16 pb-8" role="contentinfo">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center gap-2 font-bold text-lg text-slate-900">
              <img src={logoIcon} alt={t.footer.logoAlt} className="w-6 h-6 rounded-md" />
              Hub Links
            </div>
            <div className="text-sm text-slate-500">
              {t.footer.rights}
            </div>
            <nav className="flex gap-6" aria-label={t.footer.socialsAriaLabel}>
              <a href="#" className="text-slate-400 hover:text-brand-primary transition-colors" aria-label="Twitter">Twitter</a>
              <a href="#" className="text-slate-400 hover:text-brand-primary transition-colors" aria-label="Instagram">Instagram</a>
              <a href="#" className="text-slate-400 hover:text-brand-primary transition-colors" aria-label="GitHub">GitHub</a>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
